"use client";

import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Dialog,
  DialogContent,
  DialogTitle,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { API_URL } from "../../config";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
  ArcElement,
} from "chart.js";
import { Line, Bar, Pie } from "react-chartjs-2";
import FilterFeesComponent from "./filterFeesComponent";
import axios from "axios";
import { getCookie } from "../../function";
import getArticleService from "../../services/getArticleService";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  Filler,
  ArcElement
);

export default function Calculation() {
  const theme = useTheme();
  const [clients, setClients] = useState([]);
  const [journals, setJournals] = useState([]);
  const [publications, setPublications] = useState([]);
  const [publicationStates, setPublicationStates] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");

  // Filter states
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedJournal, setSelectedJournal] = useState("");
  const [selectedPublication, setSelectedPublication] = useState("");
  const [selectedPublicationState, setSelectedPublicationState] = useState("");
  const [selectedService, setSelectedService] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("INR");

  // UI states
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);
  const [openFilterDialog, setOpenFilterDialog] = useState(false);

  // Data states
  const [summaryData, setSummaryData] = useState(null);
  const [chartData, setChartData] = useState(null);

  const API_FILTERED_DATA_URL = "/api/fees-data";

  const [chartType, setChartType] = useState("line");
  const [chartView, setChartView] = useState("all");

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  const id = getCookie("id");

  useEffect(() => {
    const fetchAllOptions = async () => {
      setLoadingOptions(true);
      setError(null);
      try {
        const response = await axios.get(API_URL + "/all-options", {
          headers: {
            "x-api-key": "JjgeTensKk20J7GIxBfMSaA25Aricvmr1HYmjxpS",
          },
        });

        const data = response.data;
        const parsedData = JSON.parse(data);
        const result = JSON.parse(parsedData.body);

        setClients(result.data.clients || []);
        setJournals(result.data.journals || []);
        setPublications(result.data.publications || []);
        setServices(result.data.services || []);
      } catch (error) {
        setError(error.message || "Failed to fetch dropdown options");
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchAllOptions();
  }, []);

  // Add new useEffect for initial data fetch
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoadingData(true);
      setError(null);
      setSummaryData(null);
      setChartData(null);

      try {
        const url = `${API_URL}/articles/statsdashboard?id=${encodeURIComponent(
          id
        )}`;
        const res = await axios.post(
          url,
          {
            clientId: null,
            journalName: null,
            PublicationId: null,
            publicationState: null,
            service: null,
            country: null,
            state: null,
            dateRange: null,
          },
          {
            headers: {
              "Content-Type": "application/json",
              "x-api-key": "JjgeTensKk20J7GIxBfMSaA25Aricvmr1HYmjxpS",
              Authorization: getCookie("token"),
            },
          }
        );

        const resdata = JSON.parse(res.data);
        const data = JSON.parse(resdata.body);

        console.log("data", data);
        setSummaryData(data.additionalData);

        if (Array.isArray(data.timeSeries) && data.timeSeries.length > 0) {
          const labels = data.timeSeries.map((item) =>
            new Date(item.date).toLocaleDateString()
          );
          setChartData({
            labels,
            datasets: [
              {
                label: "Total Revenue Incl GST",
                data: data.timeSeries.map(
                  (item) => item.totalRevenueInclGST ?? 0
                ),
                borderColor: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                fill: true,
                tension: 0.3,
                pointRadius: 3,
              },
              {
                label: "Total Revenue Excl GST",
                data: data.timeSeries.map(
                  (item) => item.totalRevenueExclGST ?? 0
                ),
                borderColor: theme.palette.secondary.main,
                backgroundColor: alpha(theme.palette.secondary.main, 0.2),
                fill: true,
                tension: 0.3,
                pointRadius: 3,
              },
              {
                label: "GST Amount",
                data: data.timeSeries.map(
                  (item) =>
                    (item.totalRevenueInclGST ?? 0) -
                    (item.totalRevenueExclGST ?? 0)
                ),
                borderColor: theme.palette.error.main,
                backgroundColor: alpha(theme.palette.error.main, 0.2),
                fill: true,
                tension: 0.3,
                pointRadius: 3,
              },
              {
                label: "Remaining Fees",
                data: data.timeSeries.map((item) => item.remainingFees ?? 0),
                borderColor: theme.palette.warning.main,
                backgroundColor: alpha(theme.palette.warning.main, 0.2),
                fill: true,
                tension: 0.3,
                pointRadius: 3,
              },
              {
                label: "Fees Received",
                data: data.timeSeries.map((item) => item.feesReceived ?? 0),
                borderColor: theme.palette.success.main,
                backgroundColor: alpha(theme.palette.success.main, 0.2),
                fill: true,
                tension: 0.3,
                pointRadius: 3,
              },
              {
                label: "Discount Amount",
                data: data.timeSeries.map((item) => item.discountAmount ?? 0),
                borderColor: theme.palette.info.dark,
                backgroundColor: alpha(theme.palette.info.dark, 0.2),
                fill: true,
                tension: 0.3,
                pointRadius: 3,
              },
              {
                label: "Refund Amount",
                data: data.timeSeries.map((item) => item.refundAmount ?? 0),
                borderColor: theme.palette.error.dark,
                backgroundColor: alpha(theme.palette.error.dark, 0.2),
                fill: true,
                tension: 0.3,
                pointRadius: 3,
              },
              {
                label: "Additional Amount Received",
                data: data.timeSeries.map(
                  (item) => item.additionalAmountReceived ?? 0
                ),
                borderColor: theme.palette.info.main,
                backgroundColor: alpha(theme.palette.info.main, 0.2),
                fill: true,
                tension: 0.3,
                pointRadius: 3,
              },
            ],
          });
        } else {
          setChartData(null);
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoadingData(false);
      }
    };

    fetchInitialData();
  }, [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.warning.main,
    theme.palette.success.main,
  ]);

  const handleApply = async (filters) => {
    setOpenFilterDialog(false);
    setLoadingData(true);
    setError(null);
    setSummaryData(null);
    setChartData(null);

    const payload = {
      clientId: filters.selectedClient || null,
      journalName: filters.selectedJournal || null,
      PublicationId: filters.selectedPublication || null,
      publicationState: filters.selectedPublicationState || null,
      service: filters.selectedService || null,
      country: filters.selectedCountry || null,
      state: filters.selectedState || null,
      currency: filters.selectedCurrency || "INR",
      dateRange:
        filters.startDate && filters.endDate
          ? [
              new Date(filters.startDate).toISOString(),
              new Date(filters.endDate).toISOString(),
            ]
          : null,
    };

    try {
      const url = `${API_URL}/articles/statsdashboard?id=${encodeURIComponent(
        id
      )}`;
      const res = await axios.post(url, payload, {
        headers: {
          "Content-Type": "application/json",
          "x-api-key": "JjgeTensKk20J7GIxBfMSaA25Aricvmr1HYmjxpS",
          Authorization: getCookie("token"),
        },
      });

      const resdata = JSON.parse(res.data);
      const data = JSON.parse(resdata.body);

      setSummaryData(data.additionalData);

      if (Array.isArray(data.timeSeries) && data.timeSeries.length > 0) {
        const labels = data.timeSeries.map((item) =>
          new Date(item.date).toLocaleDateString()
        );
        setChartData({
          labels,
          datasets: [
            {
              label: "Total Revenue Incl GST",
              data: data.timeSeries.map(
                (item) => item.totalRevenueInclGST ?? 0
              ),
              borderColor: theme.palette.primary.main,
              backgroundColor: alpha(theme.palette.primary.main, 0.2),
              fill: true,
              tension: 0.3,
              pointRadius: 3,
            },
            {
              label: "Total Revenue Excl GST",
              data: data.timeSeries.map(
                (item) => item.totalRevenueExclGST ?? 0
              ),
              borderColor: theme.palette.secondary.main,
              backgroundColor: alpha(theme.palette.secondary.main, 0.2),
              fill: true,
              tension: 0.3,
              pointRadius: 3,
            },
            {
              label: "GST Amount",
              data: data.timeSeries.map(
                (item) =>
                  (item.totalRevenueInclGST ?? 0) -
                  (item.totalRevenueExclGST ?? 0)
              ),
              borderColor: theme.palette.error.main,
              backgroundColor: alpha(theme.palette.error.main, 0.2),
              fill: true,
              tension: 0.3,
              pointRadius: 3,
            },
            {
              label: "Remaining Fees",
              data: data.timeSeries.map((item) => item.remainingFees ?? 0),
              borderColor: theme.palette.warning.main,
              backgroundColor: alpha(theme.palette.warning.main, 0.2),
              fill: true,
              tension: 0.3,
              pointRadius: 3,
            },
            {
              label: "Fees Received",
              data: data.timeSeries.map((item) => item.feesReceived ?? 0),
              borderColor: theme.palette.success.main,
              backgroundColor: alpha(theme.palette.success.main, 0.2),
              fill: true,
              tension: 0.3,
              pointRadius: 3,
            },
            {
              label: "Discount Amount",
              data: data.timeSeries.map((item) => item.discountAmount ?? 0),
              borderColor: theme.palette.info.dark,
              backgroundColor: alpha(theme.palette.info.dark, 0.2),
              fill: true,
              tension: 0.3,
              pointRadius: 3,
            },
            {
              label: "Refund Amount",
              data: data.timeSeries.map((item) => item.refundAmount ?? 0),
              borderColor: theme.palette.error.dark,
              backgroundColor: alpha(theme.palette.error.dark, 0.2),
              fill: true,
              tension: 0.3,
              pointRadius: 3,
            },
            {
              label: "Additional Amount Received",
              data: data.timeSeries.map(
                (item) => item.additionalAmountReceived ?? 0
              ),
              borderColor: theme.palette.info.main,
              backgroundColor: alpha(theme.palette.info.main, 0.2),
              fill: true,
              tension: 0.3,
              pointRadius: 3,
            },
          ],
        });
      } else {
        setChartData(null);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingData(false);
    }
  };

  const formatFilterValue = (val, list) => {
    const item = list.find((i) => i.id === val);
    return item?.name || "All";
  };

  const formatCurrency = (value) => {
    const currencySymbols = {
      INR: "₹",
      USD: "$",
      AED: "د.إ",
    };

    const currencyFormatters = {
      INR: new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      USD: new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      AED: new Intl.NumberFormat("en-AE", {
        style: "currency",
        currency: "AED",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    };

    const formatter =
      currencyFormatters[selectedCurrency] || currencyFormatters["INR"];
    return formatter.format(value || 0);
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "nearest", intersect: false },
    plugins: {
      legend: {
        position: "top",
        labels: {
          boxWidth: 12,
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      tooltip: {
        backgroundColor: alpha(theme.palette.background.paper, 0.9),
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        padding: 12,
        boxPadding: 6,
        usePointStyle: true,
        callbacks: {
          label: (context) => {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            if (context.parsed.y !== null) {
              label += formatCurrency(context.parsed.y);
            }
            return label;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        },
      },
      y: {
        grid: {
          borderDash: [2, 4],
          color: alpha(theme.palette.text.secondary, 0.15),
        },
        ticks: {
          callback: (value) => formatCurrency(value),
        },
      },
    },
  };

  const hasActiveFilters = () => {
    return (
      selectedClient ||
      selectedJournal ||
      selectedPublication ||
      selectedPublicationState ||
      selectedService ||
      selectedCountry ||
      selectedState ||
      selectedCurrency !== "INR" ||
      startDate ||
      endDate
    );
  };

  const handleChartTypeChange = (event, newChartType) => {
    if (newChartType !== null) {
      setChartType(newChartType);
    }
  };

  const handleChartViewChange = (event, newView) => {
    if (newView !== null) {
      setChartView(newView);
    }
  };

  const getFilteredDatasets = () => {
    if (!chartData) return [];

    switch (chartView) {
      case "revenue":
        return chartData.datasets.filter(
          (ds) => ds.label.includes("Revenue") || ds.label.includes("GST")
        );
      case "fees":
        return chartData.datasets.filter((ds) => ds.label.includes("Fees"));
      default:
        return chartData.datasets;
    }
  };

  const renderChart = () => {
    if (!chartData) return null;

    const filteredData = {
      ...chartData,
      datasets: getFilteredDatasets(),
    };

    // Common options for all chart types
    const commonOptions = {
      ...chartOptions,
      plugins: {
        ...chartOptions.plugins,
        title: {
          display: true,
          text: `${
            chartView === "all"
              ? "All"
              : chartView === "revenue"
              ? "Revenue"
              : "Fees"
          } Over Time`,
          font: {
            size: 16,
            weight: "bold",
          },
        },
      },
    };

    try {
      switch (chartType) {
        case "bar":
          return (
            <Bar
              options={{
                ...commonOptions,
                scales: {
                  ...commonOptions.scales,
                  y: {
                    ...commonOptions.scales.y,
                    beginAtZero: true,
                  },
                },
              }}
              data={filteredData}
            />
          );

        case "pie":
          // For pie chart, we'll show the latest data point
          const latestData = {
            labels: filteredData.datasets.map((ds) => ds.label),
            datasets: [
              {
                data: filteredData.datasets.map(
                  (ds) => ds.data[ds.data.length - 1] || 0
                ),
                backgroundColor: filteredData.datasets.map(
                  (ds) => ds.backgroundColor
                ),
                borderColor: filteredData.datasets.map((ds) => ds.borderColor),
                borderWidth: 1,
              },
            ],
          };

          // Custom options for pie chart
          const pieOptions = {
            ...commonOptions,
            plugins: {
              ...commonOptions.plugins,
              tooltip: {
                ...commonOptions.plugins.tooltip,
                callbacks: {
                  label: (context) => {
                    const label = context.label || "";
                    const value = context.raw || 0;
                    const total = context.dataset.data.reduce(
                      (a, b) => a + b,
                      0
                    );
                    const percentage = ((value / total) * 100).toFixed(1);
                    return `${label}: ${formatCurrency(
                      value
                    )} (${percentage}%)`;
                  },
                },
              },
            },
            maintainAspectRatio: false,
            responsive: true,
          };

          return <Pie data={latestData} options={pieOptions} />;

        default:
          return (
            <Line
              options={{
                ...commonOptions,
                scales: {
                  ...commonOptions.scales,
                  y: {
                    ...commonOptions.scales.y,
                    beginAtZero: true,
                  },
                },
              }}
              data={filteredData}
            />
          );
      }
    } catch (error) {
      console.error("Chart rendering error:", error);
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            color: "error.main",
          }}
        >
          <Typography>
            Error rendering chart. Please try a different view.
          </Typography>
        </Box>
      );
    }
  };

  const generatePreSignedUrlsForExcel = async (records) => {
    const urlsMap = {};

    for (const record of records) {
      const payments = record.payments || [];

      for (const payment of payments) {
        // Invoice
        if (payment.invoiceKey && !urlsMap[payment.invoiceKey]) {
          try {
            const data = await getArticleService.getFilePreSIgnedUrl({
              key: payment.invoiceKey,
            });
            urlsMap[payment.invoiceKey] = data; // store URL
          } catch (error) {
            console.error("Error fetching invoice URL:", error);
          }
        }

        // Payment Proofs
        if (
          payment.paymentProofFileKeys &&
          Array.isArray(payment.paymentProofFileKeys)
        ) {
          for (const proof of payment.paymentProofFileKeys) {
            if (!urlsMap[proof]) {
              try {
                const data = await getArticleService.getFilePreSIgnedUrl({
                  key: proof,
                });
                urlsMap[proof] = data; // store URL
              } catch (error) {
                console.error("Error fetching proof URL:", error);
              }
            }
          }
        }
      }
    }

    return urlsMap;
  };

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);

    const payload = {
      clientId: selectedClient || null,
      journalName: selectedJournal || null,
      PublicationId: selectedPublication || null,
      service: selectedService || null,
      country: selectedCountry || null,
      state: selectedState || null,
      currency: selectedCurrency || "INR",
      dateRange:
        startDate && endDate
          ? [new Date(startDate).toISOString(), new Date(endDate).toISOString()]
          : null,
    };

    try {
      const response = await axios.post(
        API_URL + "/articles/getPaymentExportData",
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            "x-api-key": "JjgeTensKk20J7GIxBfMSaA25Aricvmr1HYmjxpS",
            Authorization: getCookie("token"),
          },
        }
      );

      const resdata = JSON.parse(response.data);
      const data = JSON.parse(resdata.body);

      if (data.data && Array.isArray(data.data)) {
        const urlsMap = await generatePreSignedUrlsForExcel(data.data);

        const processedData = data.data.map((item) => {
          const processedItem = {};
          for (const [key, value] of Object.entries(item)) {
            if (key === "payments") {
              // Preserve raw payments; we'll parse/flatten below
              processedItem[key] = value;
              continue;
            }
            if (Array.isArray(value)) {
              processedItem[key] = value
                .map((v) => (typeof v === "object" ? JSON.stringify(v) : v))
                .join("; ");
            } else if (typeof value === "object" && value !== null) {
              // Convert object to string
              processedItem[key] = JSON.stringify(value);
            } else {
              processedItem[key] = value;
            }
          }
          return processedItem;
        });

        // Create rows for each payment
        const allRows = [];
        const parsePayments = (raw) => {
          if (!raw) return null;
          if (Array.isArray(raw)) return raw;
          if (typeof raw === "object") return [raw];
          if (typeof raw === "string") {
            try {
              const parsed = JSON.parse(raw);
              return Array.isArray(parsed) ? parsed : [parsed];
            } catch (_) {
              const matches = raw.match(/\{[\s\S]*?\}/g);
              if (matches && matches.length) {
                const arr = [];
                for (const objStr of matches) {
                  try {
                    arr.push(JSON.parse(objStr));
                  } catch (_) {}
                }
                if (arr.length) return arr;
              }
              return null;
            }
          }
          return null;
        };

        processedData.forEach((item) => {
          if (item.payments) {
            const payments = parsePayments(item.payments);
            if (payments && payments.length) {
              payments.forEach((payment) => {
                const paymentRow = { ...item };
                Object.entries(payment).forEach(([key, value]) => {
                  if (Array.isArray(value)) {
                    paymentRow[`payment_${key}`] = value.join("; ");
                  } else {
                    paymentRow[`payment_${key}`] = value;
                  }
                });
                allRows.push(paymentRow);
              });
            } else {
              allRows.push(item);
            }
          } else {
            allRows.push(item);
          }
        });

        // Helpers for user-friendly formatting
        const formatDateValue = (value) => {
          if (!value) return "";
          const date = new Date(value);
          if (Number.isNaN(date.getTime())) {
            return String(value);
          }
          return date.toLocaleDateString("en-IN");
        };

        const formatNumberValue = (value) => {
          if (value === null || value === undefined || value === "") return "";
          const num = Number(value);
          if (Number.isNaN(num)) return String(value);
          return num.toLocaleString("en-IN", { maximumFractionDigits: 2 });
        };

        const toCsvSafe = (val) => {
          if (val === null || val === undefined) return "";
          const stringValue = String(val);
          if (
            stringValue.includes(",") ||
            stringValue.includes('"') ||
            stringValue.includes("\n")
          ) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        };

        // Resolve clientId -> client name using loaded options
        const getClientNameById = (idVal) => {
          if (!idVal) return "";
          const match = clients?.find?.((c) => String(c.id) === String(idVal));
          const name = match.first_name + match.last_name;
          return name || "";
        };

        // Resolve clientId -> client name using loaded options
        const getPublicationNameById = (idVal) => {
          if (!idVal) return "";
          const publicationmatch = publications?.find?.(
            (p) => String(p.id) === String(idVal)
          );
          return publicationmatch?.name || "";
        };

        // Safely read a field from the first payment; supports object/array/stringified JSON
        // const getFirstPaymentField = (row, key) => {
        //   try {
        //     let paymentsSrc = row?.payments;
        //     if (!paymentsSrc) return undefined;
        //     if (typeof paymentsSrc === "string") {
        //       try {
        //         paymentsSrc = JSON.parse(paymentsSrc);
        //       } catch (_) {
        //         return undefined;
        //       }
        //     }
        //     if (Array.isArray(paymentsSrc)) {
        //       if (paymentsSrc.length === 0) return undefined;
        //       const match = paymentsSrc.find(
        //         (p) => p && typeof p === "object" && p[key] !== undefined
        //       );
        //       return match ? match[key] : undefined;
        //     }
        //     if (paymentsSrc && typeof paymentsSrc === "object") {
        //       return paymentsSrc[key];
        //     }
        //     return undefined;
        //   } catch (_) {
        //     return undefined;
        //   }
        // };

        // ✅ Modified to return the correct payment based on current row
        const getFirstPaymentField = (row, key) => {
          try {
            let paymentsSrc = row?.payments;
            if (!paymentsSrc) return undefined;

            // Parse stringified payments if necessary
            if (typeof paymentsSrc === "string") {
              try {
                paymentsSrc = JSON.parse(paymentsSrc);
              } catch (_) {
                paymentsSrc = [];
              }
            }

            // When exporting, each row already includes payment_ fields
            // and belongs to one specific payment (flattened above in your allRows logic)
            // So we can directly prioritize `payment_` prefixed keys first
            if (row[`payment_${key}`] !== undefined) {
              return row[`payment_${key}`];
            }

            // If payments is an array (fallback for safety)
            if (Array.isArray(paymentsSrc)) {
              if (paymentsSrc.length === 0) return undefined;
              const first = paymentsSrc[0];
              if (first && typeof first === "object" && key in first) {
                return first[key];
              }
            }

            // Or if it's a single object
            if (
              paymentsSrc &&
              typeof paymentsSrc === "object" &&
              key in paymentsSrc
            ) {
              return paymentsSrc[key];
            }

            return undefined;
          } catch (err) {
            return undefined;
          }
        };

        // Column mapping: order + readable headers + value getters with fallbacks
        const columns = [
          {
            header: "Email address",
            getValue: (r) => r.email || r.emailId || r.emailid || "",
          },
          {
            header: "NAME OF PUBLICATION",
            getValue: (r) => getPublicationNameById(r.PublicationId) || "",
          },
          {
            header: "Candidate Name",
            getValue: (r) =>
              r.candidateName || getClientNameById(r.clientId) || "",
          },

          {
            header: "Service Charge",
            getValue: (r) =>
              r.serviceCharge || r.service || r.serviceName || "",
          },
          {
            header: "Journal Name",
            getValue: (r) => r.journalName || "",
          },

          {
            header: "CREDIT DATE",
            getValue: (r) =>
              formatDateValue(getFirstPaymentField(r, "creditDate") || "NA"),
          },
          {
            header: "UTR",
            getValue: (r) => r.utrNo || getFirstPaymentField(r, "utrNo") || "",
          },

          {
            header: "Article id",
            getValue: (r) => r.id || "",
          },
          {
            header: "Payment Mode",
            getValue: (r) =>
              r.payment_paymentMode ||
              r.paymentMode ||
              getFirstPaymentField(r, "paymentMode") ||
              "",
          },
          {
            header: "Payment Date",
            getValue: (r) =>
              formatDateValue(getFirstPaymentField(r, "paymentDate") || ""),
          },
          // {
          //   header: "Payment Proof",
          //   getValue: (r) => {
          //     const fp = getFirstPaymentField(r, "paymentProofFileKeys");
          //     const joined = Array.isArray(fp) ? fp.join("; ") : fp;
          //     return (
          //       r.payment_paymentProofFileKeys ||
          //       r.payment_proof ||
          //       joined ||
          //       ""
          //     );
          //   },
          // },
          {
            header: "Payment Proof (Click to Open)",
            getValue: (r) => {
              const fp = getFirstPaymentField(r, "paymentProofFileKeys");
              const proofs = Array.isArray(fp) ? fp : fp ? [fp] : [];

              const urls = proofs
                .map((key) => {
                  const url = urlsMap[key];
                  if (!url) return "";
                  // Create clickable link format for Excel
                  return `=HYPERLINK("${url}", "View Payment Proof")`;
                })
                .filter(Boolean)
                .join(" ; "); // separate multiple links

              return urls || "No payment proof available";
            },
          },

          // {
          //   header: "Invoice Raised",
          //   getValue: (r) =>
          //     r.payment_invoiceRaised ??
          //     r.invoiceRaised ??
          //     getFirstPaymentField(r, "invoiceKey") ??
          //     "",
          // },

          {
            header: "Invoice (Click to Open)",
            getValue: (r) => {
              const invoiceKey = getFirstPaymentField(r, "invoiceKey");
              if (!invoiceKey) return "No invoice available";

              const url = urlsMap[invoiceKey];
              if (!url) return "Invoice URL not available";

              // Create clickable link format for Excel
              return `=HYPERLINK("${url}", "View Invoice")`;
            },
          },

          {
            header: "Currency Type ",
            getValue: (r) => formatDateValue(r.currency || ""),
          },
          {
            header: "Total Fees (INR)",
            getValue: (r) => formatNumberValue(r.totalfees),
          },
          {
            header: "GST CHARGED",
            getValue: (r) => formatNumberValue(r.taxRate),
          },
          {
            header: "Fees Received (INR)",
            getValue: (r) =>
              formatNumberValue(
                r.payment_amount ??
                  r.amount ??
                  getFirstPaymentField(r, "amount") ??
                  r.feesReceived
              ),
          },
          {
            header: "Remaining Fees (INR)",
            getValue: (r) => formatNumberValue(r.remaningFees),
          },
          {
            header: "Author Name",
            getValue: (r) => r.authorName || "",
          },
          {
            header: "University",
            getValue: (r) => r.authorUniversity || r.university || "",
          },
          {
            header: "State",
            getValue: (r) => r.state || "",
          },
          {
            header: "GSTIN No. of Author",
            getValue: (r) => r.authorGstNo || "",
          },
          {
            header: "Refunded Fee",
            getValue: (r) => formatNumberValue(r.refundAmount || r.refundedFee),
          },
          {
            header: "Agent fee or Normal",
            getValue: (r) => r.feeType || "",
          },
          {
            header: "Regular or Special",
            getValue: (r) => r.publicationType || "",
          },
          {
            header: "Comments",
            getValue: (r) => r.comments || r.remark || "",
          },
          {
            header: "PAYER NAME",
            getValue: (r) => r.payerName || "",
          },
          {
            header: "Payment received (USD) [write NA otherwise]",
            getValue: (r) => r.currency || "",
          },
          {
            header: "Mark Inhouse or Other",
            getValue: (r) => r.invoiceType || "",
          },
        ];

        const headers = columns.map((c) => c.header);
        const colCount = headers.length;

        // Create date range row - first row of CSV
        let dateRangeRow = "";
        let filename = "";

        if (
          payload.dateRange &&
          payload.dateRange.length === 2 &&
          payload.dateRange[0] &&
          payload.dateRange[1]
        ) {
          const [from, to] = payload.dateRange;
          const formattedFrom = new Date(from).toLocaleDateString("en-IN");
          const formattedTo = new Date(to).toLocaleDateString("en-IN");
          // Create a row with date range in first column and empty cells for remaining columns
          // Add symbols to indicate importance
          dateRangeRow =
            `"*** DATE RANGE: ${formattedFrom} to ${formattedTo} ***"` +
            ",".repeat(colCount - 1);
          filename = `payments_${formattedFrom}_to_${formattedTo}.csv`;
        } else {
          const currentDate = new Date()
            .toLocaleDateString("en-IN")
            .replace(/\//g, "-");
          dateRangeRow =
            `"*** COMPLETE DATA ***" ${currentDate}` + ",".repeat(colCount - 1);
          filename = `payments_complete_data_${currentDate}.csv`;
        }

        // Convert to CSV with date range as first row, headers as second row
        const csvRows = [
          console.log(dateRangeRow),
          dateRangeRow, // First row: date range or "Complete Data"
          headers.map((h) => toCsvSafe(h)).join(","), // Second row: headers
          ...allRows.map((row) =>
            columns.map((c) => toCsvSafe(c.getValue(row))).join(",")
          ), // Remaining rows: data
        ];

        const csvContent = csvRows.join("\n");

        // Create and download CSV file
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        // link.download = `payments_export_${
        //   new Date().toISOString().split("T")[0]
        // }.csv`;
        link.download = filename;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      setExportError(error.message || "Failed to export data");
      console.error("Export error:", error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, pb: 12 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            fontSize: { xs: "1.5rem", md: "2rem" },
            color: theme.palette.primary.main,
          }}
        >
          Fees Dashboard
        </Typography>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Refresh data">
            <IconButton
              onClick={() =>
                handleApply({
                  selectedClient,
                  selectedJournal,
                  selectedPublication,
                  selectedPublicationState,
                  selectedService,
                  selectedCountry,
                  selectedState,
                  selectedCurrency,
                  startDate,
                  endDate,
                })
              }
              disabled={loadingData}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<FilterAltIcon />}
            onClick={() => setOpenFilterDialog(true)}
            color="primary"
            size="small"
          >
            {hasActiveFilters() ? "Edit Filters" : "Add Filters"}
          </Button>
          <Button
            variant="contained"
            startIcon={
              exporting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <FileDownloadIcon />
              )
            }
            onClick={handleExport}
            color="secondary"
            size="small"
            disabled={exporting}
          >
            {exporting ? "Exporting..." : "Export as CSV"}
          </Button>
        </Box>
      </Box>

      {error && (
        <Paper
          elevation={0}
          sx={{
            mt: 2,
            mb: 3,
            p: 2,
            backgroundColor: alpha(theme.palette.error.main, 0.1),
            border: `1px solid ${theme.palette.error.main}`,
            borderRadius: 1,
          }}
        >
          <Typography color="error">{error}</Typography>
        </Paper>
      )}

      {exportError && (
        <Paper
          elevation={0}
          sx={{
            mt: 2,
            mb: 3,
            p: 2,
            backgroundColor: alpha(theme.palette.error.main, 0.1),
            border: `1px solid ${theme.palette.error.main}`,
            borderRadius: 1,
          }}
        >
          <Typography color="error">{exportError}</Typography>
        </Paper>
      )}

      {loadingData && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 8 }}>
          <CircularProgress size={60} thickness={4} />
        </Box>
      )}

      {summaryData && !loadingData && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6} lg={2.4}>
            <Card
              elevation={2}
              sx={{
                height: "100%",
                transition: "transform 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: theme.shadows[4],
                },
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  Total Revenue (Incl GST)
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 600, color: theme.palette.primary.main }}
                >
                  {formatCurrency(summaryData.totalRevenueInclGST)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={2.4}>
            <Card
              elevation={2}
              sx={{
                height: "100%",
                transition: "transform 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: theme.shadows[4],
                },
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  Total Revenue (Excl GST)
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 600, color: theme.palette.secondary.main }}
                >
                  {formatCurrency(summaryData.totalRevenueExclGST)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={2.4}>
            <Card
              elevation={2}
              sx={{
                height: "100%",
                transition: "transform 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: theme.shadows[4],
                },
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  GST Amount
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 600, color: theme.palette.error.main }}
                >
                  {formatCurrency(
                    summaryData.totalRevenueInclGST -
                      summaryData.totalRevenueExclGST
                  )}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={2.4}>
            <Card
              elevation={2}
              sx={{
                height: "100%",
                transition: "transform 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: theme.shadows[4],
                },
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  Remaining Fees
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 600, color: theme.palette.warning.main }}
                >
                  {formatCurrency(summaryData.remainingFees)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={2.4}>
            <Card
              elevation={2}
              sx={{
                height: "100%",
                transition: "transform 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: theme.shadows[4],
                },
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  Fees Received
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 600, color: theme.palette.success.main }}
                >
                  {formatCurrency(summaryData.feesReceived)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={2.4}>
            <Card
              elevation={2}
              sx={{
                height: "100%",
                transition: "transform 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: theme.shadows[4],
                },
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  Total Discount Amount
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 600, color: theme.palette.info.dark }}
                >
                  {formatCurrency(summaryData.totalDiscountAmount)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={2.4}>
            <Card
              elevation={2}
              sx={{
                height: "100%",
                transition: "transform 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: theme.shadows[4],
                },
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  Total Refund Amount
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 600, color: theme.palette.error.dark }}
                >
                  {formatCurrency(summaryData.totalRefundAmount)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={2.4}>
            <Card
              elevation={2}
              sx={{
                height: "100%",
                transition: "transform 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: theme.shadows[4],
                },
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  Total Additional Amount Received
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 600, color: theme.palette.info.main }}
                >
                  {formatCurrency(summaryData.totalAdditionalAmountReceived)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {chartData && !loadingData && (
        <Paper
          elevation={3}
          sx={{
            mt: 4,
            p: 3,
            maxWidth: 1200,
            margin: "auto",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              Fees and Revenue Over Time
            </Typography>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <ToggleButtonGroup
                value={chartView}
                exclusive
                onChange={handleChartViewChange}
                size="small"
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="revenue">Revenue</ToggleButton>
                <ToggleButton value="fees">Fees</ToggleButton>
              </ToggleButtonGroup>
              <ToggleButtonGroup
                value={chartType}
                exclusive
                onChange={handleChartTypeChange}
                size="small"
              >
                <ToggleButton value="line">Line</ToggleButton>
                <ToggleButton value="bar">Bar</ToggleButton>
                <ToggleButton value="pie">Pie</ToggleButton>
              </ToggleButtonGroup>
              <Tooltip title="Chart shows the trend of fees and revenue over the selected time period">
                <IconButton size="small">
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Divider sx={{ mb: 3 }} />
          <Box sx={{ height: 400 }}>{renderChart()}</Box>
        </Paper>
      )}

      {/* Filter Modal */}
      <Dialog
        open={openFilterDialog}
        onClose={() => setOpenFilterDialog(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          elevation: 5,
          sx: {
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: "white",
            fontWeight: 600,
          }}
        >
          Edit Filters
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, md: 3 }, mt: 1 }}>
          <FilterFeesComponent
            clients={clients}
            journals={journals}
            publications={publications}
            publicationStates={publicationStates}
            services={services}
            selectedCountry={selectedCountry}
            setSelectedCountry={setSelectedCountry}
            selectedState={selectedState}
            setSelectedState={setSelectedState}
            selectedClient={selectedClient}
            setSelectedClient={setSelectedClient}
            selectedJournal={selectedJournal}
            setSelectedJournal={setSelectedJournal}
            selectedPublication={selectedPublication}
            setSelectedPublication={setSelectedPublication}
            selectedPublicationState={selectedPublicationState}
            setSelectedPublicationState={setSelectedPublicationState}
            selectedService={selectedService}
            setSelectedService={setSelectedService}
            selectedCurrency={selectedCurrency}
            setSelectedCurrency={setSelectedCurrency}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            loadingOptions={loadingOptions}
            onApply={handleApply}
            loadingData={loadingData}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
